import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { cn } from './cn';
import { Wordmark } from './Wordmark';

export interface HeaderNavItem {
  to: string;
  label: string;
  end?: boolean;
}

export interface AppHeaderProps {
  /** ".app" | ".men" | ".tv" — drives the wordmark suffix only. */
  surface?: string;
  homeTo: string;
  items: HeaderNavItem[];
  /**
   * `nav-center`  — `.app`: nav holds the middle, search lives behind an icon.
   * `search-center` — `.men`: search holds the middle, nav sits right.
   */
  layout?: 'nav-center' | 'search-center';
  /** The SearchField instance, when this surface keeps search visible. */
  search?: ReactNode;
  /** Slot beside the wordmark — live counts, surface switchers. */
  lead?: ReactNode;
  /** Right-hand utilities — settings, account, log out. */
  utility?: ReactNode;
  /**
   * Render the 56px mobile bar as well as the 64px desktop one — Screen
   * System §2.
   *
   * OFF BY DEFAULT, and it has to stay that way. The kit syncs wholesale into
   * nine live apps, several of which already draw their own mobile header. If
   * this defaulted on, the next `pnpm hottr:sync` would silently give them two
   * stacked headers and nobody would find out from this repo. Opt in per app,
   * after removing the one it has.
   */
  mobile?: boolean;
  /**
   * Replaces the wordmark with a WorldSwitcher. The suffix IS the control
   * (§2), so this occupies the same slot rather than adding furniture beside
   * it. When set, `homeTo` and `surface` are unused.
   *
   * The switcher's panel positions against this header, which is `sticky` and
   * therefore already a containing block — no extra wrapper needed.
   */
  switcher?: ReactNode;
  className?: string;
}

const navItemClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'relative inline-flex h-11 items-center justify-center px-3',
    'font-display text-[11px] font-extrabold uppercase tracking-hottr-label',
    'transition-colors duration-base ease-mechanical',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hottr-pink focus-visible:ring-offset-2 focus-visible:ring-offset-hottr-ink',
    isActive ? 'text-white' : 'text-white/55 hover:text-white',
  );

/**
 * The shared header chassis.
 *
 * Fixed across the ecosystem: 64px height, wordmark placement and scale,
 * typeface and sizing, the pink underline active state, icon style, spacing
 * rhythm, and the glass-on-ink treatment.
 *
 * Free to differ: the destinations, and whether search is persistent.
 *   .app   FEED · GRID · MESSAGES · WORLD
 *   .men   BROWSE · ALL · SAVED · ACCOUNT
 *
 * The pink underline came from `.men`, where the active state read far more
 * clearly than `.app`'s near-invisible one. It is now both.
 */
export const AppHeader = ({
  surface,
  homeTo,
  items,
  layout = 'nav-center',
  search,
  lead,
  utility,
  mobile = false,
  switcher,
  className,
}: AppHeaderProps) => {
  const nav = (
    <nav
      className="flex flex-nowrap items-center gap-1"
      aria-label="Primary"
    >
      {items.map((item) => (
        <NavLink key={item.to} to={item.to} end={item.end} className={navItemClass}>
          {({ isActive }) => (
            <>
              {item.label}
              {isActive && (
                <span
                  aria-hidden
                  className="absolute inset-x-3 bottom-0 h-px bg-hottr-pink shadow-neon-soft"
                />
              )}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <header
      className={cn(
        'sticky top-0 z-40 md:grid md:h-16',
        // 56px mobile / 64px desktop when opted in; otherwise the original
        // desktop-only bar, byte for byte.
        mobile ? 'flex h-14 justify-between' : 'hidden h-16',
        'items-center gap-6 px-6 lg:px-10',
        'border-b border-white/[0.06] bg-hottr-ink/75 backdrop-blur-xl backdrop-saturate-150',
        // The search column. It was capped at 420px, which at desktop width is
        // a stub of a field with the whole width of the header around it and
        // nothing to do. 560 is wide enough to read a long query back and still
        // leaves the identity on the left and the utilities on the right.
        layout === 'search-center'
          ? 'grid-cols-[minmax(0,1fr)_minmax(320px,560px)_minmax(0,1fr)]'
          : 'grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-5 justify-self-start">
        {switcher ?? (
          <Link
            to={homeTo}
            className="inline-flex h-11 items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hottr-pink"
          >
            <Wordmark suffix={surface} size="lg" />
          </Link>
        )}
        {lead}
      </div>

      <div
        className={cn(
          'min-w-0 items-center justify-center',
          // `justify-self-center` shrinks a grid item to its content rather than
          // filling its track. So the search column above reserved 560px and
          // then drew a 202px field inside it — measured in Chromium at 1280px,
          // and the reason the bar read as a stub with all that empty space
          // around it. The `w-full` below could not help: it was 100% of an
          // already-shrunk box.
          //
          // Only the search column stretches. Nav is genuinely content-width
          // (its track is `auto`, not a minmax), so `nav-center` keeps centring
          // and `.app` is untouched by this.
          layout === 'search-center' ? 'justify-self-stretch' : 'justify-self-center',
          // On mobile the Orb owns navigation outright (§1) — no centre nav,
          // no tab bar. The header keeps identity and nothing else.
          mobile ? 'hidden md:flex' : 'flex',
        )}
      >
        {layout === 'search-center' ? <div className="w-full">{search}</div> : nav}
      </div>

      <div className="flex items-center gap-2 justify-self-end">
        {layout === 'search-center' ? nav : search}
        {utility}
      </div>
    </header>
  );
};
